import { QueryClient } from '@tanstack/react-query';
import { isClientError } from './apiError.js';
import { subscribeIdentity } from './identity.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // Delta from blueberry: it regex-matches "HTTP 401|403" out of the error
      // message. cusp's ApiError carries `status` as a real field, and its
      // identity failure mode is a 400 from resolveContext rather than a 401 —
      // so don't retry ANY 4xx. A malformed identity header will still be
      // malformed on the third attempt.
      retry: (count, err) => !isClientError(err) && count < 3,
    },
    mutations: { retry: 0 },
  },
});

let installed = false;

/**
 * The second half of the identity/cache guarantee.
 *
 * Scoped query keys (see `identityScope`) already make the previous tenant's
 * data unreachable after a switch. This closes the remaining hole: a request
 * already in flight carrying the OLD headers would otherwise land after the
 * switch and write its response into the old scope's cache entry. Cancel first
 * so late responses abort, then drop the whole `['identity', ...]` subtree.
 *
 * Note it removes only that subtree — `['dev', 'identities']` survives, so
 * switching identity doesn't refetch the switcher's own option list.
 */
export function installIdentityCacheReset(client: QueryClient = queryClient): void {
  if (installed) return;
  installed = true;
  subscribeIdentity(() => {
    void client.cancelQueries({ queryKey: ['identity'] });
    client.removeQueries({ queryKey: ['identity'] });
  });
}
